import type { ProviderQuote } from '../../features/catalog/model';
import { Icon } from '../../components/Icon';
import { ProviderArtwork } from '../../components/ProviderArtwork';
import { PriceTag } from '../../components/PriceTag';
import { fmtCallSample, fmtMs, fmtReq, pctFine, pct } from '../../lib/format';
import { m } from '../../paraglide/messages';

export const hasUptimeMetric = (provider: ProviderQuote): boolean =>
  provider.uptimeSample === undefined ? (provider.requests ?? 0) > 0 : provider.uptimeSample > 0;

export const hasLatencyMetric = (provider: ProviderQuote): boolean =>
  provider.latencySample === undefined ? provider.latencyMs > 0 : provider.latencySample > 0;

export const servedCount = (provider: ProviderQuote): number =>
  provider.servedRequests ?? provider.requests ?? 0;

export const providerKey = (provider: ProviderQuote) => provider.id;

const showMs = (provider: ProviderQuote) =>
  hasLatencyMetric(provider) ? fmtMs(provider.latencyMs) : '-';
const showUptime = (provider: ProviderQuote) =>
  hasUptimeMetric(provider) ? pctFine(provider.uptimePct) : '-';
// Its own line under the figure: inline, "98.07% (47,691 calls)" was wider than
// the column the 800px band leaves it, and ran into the share column beside it.
const showSample = (sample?: number) =>
  sample !== undefined && sample > 0 ? (
    <span className="block text-10 text-text-3">{fmtCallSample(sample)}</span>
  ) : null;
const showReq = (provider: ProviderQuote) => {
  const requests = servedCount(provider);
  return requests > 0 ? fmtReq(requests) : '-';
};

interface ShareBarProps {
  /** this provider's share of everything the endpoint served, 0 to 1 */
  share: number;
  /** the provider calls are tried on first */
  first: boolean;
  className?: string;
}

/** The share drawn: a filled track, accent on the provider calls start at and
 *  grey on the ones that pick up the rest, with the figure beside it. */
const ShareBar = ({ share, first, className }: ShareBarProps) => (
  <span className={`flex items-center gap-2.5${className ? ` ${className}` : ''}`}>
    <span className="h-1.5 flex-1 overflow-hidden rounded-2 bg-surface-3">
      {share > 0 ? (
        <span
          className={`block h-full rounded-2 ${first ? 'bg-accent' : 'bg-text-3'}`}
          style={{ width: `${Math.max(share * 100, 2)}%` }}
        />
      ) : null}
    </span>
    <span
      className={`mono w-13 shrink-0 text-right text-11 tabular-nums ${
        first ? 'text-text' : 'text-text-3'
      }`}
    >
      {share > 0 ? pct(share * 100) : '-'}
    </span>
  </span>
);

export interface ProviderRowProps {
  provider: ProviderQuote;
  /** the column template this table is drawn on, share or solo */
  grid: string;
  /** hide the share column: one provider serves everything */
  solo: boolean;
  /** this provider's share of what the endpoint served over the window */
  share: number;
  /** the provider calls are tried on first */
  first: boolean;
  bestUptime: boolean;
  bestLatency: boolean;
  /** this source is not being called right now. The row still carries its price
   *  and its measured figures, dimmed, because they are what a reader compares
   *  it on when it comes back. */
  notServing?: boolean;
}

/** One source in the comparison table, in either list. The two lists share this
 *  row so a source cannot describe itself differently depending on whether it is
 *  being called. */
export const ProviderRow = ({
  provider,
  grid,
  solo,
  share,
  first,
  bestUptime,
  bestLatency,
  notServing,
}: ProviderRowProps) => (
  <a
    href={`/providers/${provider.source.id}`}
    className={`${grid} group min-h-17 border-b border-rule px-4 py-3 last:rounded-b-12 last:border-b-0 hover:bg-wash sm:px-5${
      notServing ? ' opacity-60' : ''
    }`}
  >
    <ProviderArtwork source={provider.source} size={40} />
    <span className="flex min-w-0 flex-col gap-1">
      <span className="truncate text-14 font-medium group-hover:text-accent">
        {provider.source.name}
      </span>
      {notServing ? (
        <span className="mono w-fit rounded-md border border-rule bg-surface-3 px-1.5 py-px text-10 text-text-3">
          {m.endpoint_provider_not_serving()}
        </span>
      ) : null}
      {first ? (
        <span className="mono w-fit rounded-md border border-accent-line bg-accent-soft px-1.5 py-px text-10 text-accent">
          {m.endpoint_provider_tried_first()}
        </span>
      ) : null}
      <span className="mono flex flex-wrap items-center gap-x-3 gap-y-1 text-10 text-text-3 lg:hidden">
        <span>{m.endpoint_provider_served({ count: showReq(provider) })}</span>
        <span>{m.endpoint_provider_uptime({ value: showUptime(provider) })}</span>
        <span>{showMs(provider)} p50</span>
      </span>
      {solo ? null : (
        <ShareBar share={share} first={first} className="mt-0.5 max-w-56 lg:hidden" />
      )}
    </span>
    {solo ? null : <ShareBar share={share} first={first} className="hidden lg:flex" />}
    <span
      className={`mono hidden text-right text-11 lg:block ${bestUptime ? 'text-text' : 'text-text-3'}`}
    >
      {showUptime(provider)}
      {showSample(provider.uptimeSample)}
    </span>
    <span
      className={`mono hidden text-right text-11 lg:block ${bestLatency ? 'text-text' : 'text-text-3'}`}
    >
      {showMs(provider)}
      {showSample(provider.latencySample)}
    </span>
    <span className="mono hidden text-right text-11 text-text-3 tabular-nums lg:block">
      {showReq(provider)}
    </span>
    <span className="flex justify-end">
      <PriceTag price={provider.price} size="sm" explain flatNote={m.phase2_fix_price_flat_note()} />
    </span>
    <span className="grid size-10 place-items-center text-text-3 group-hover:text-accent">
      <Icon name="chevron-right" size={16} />
    </span>
  </a>
);
